import { assertNever } from "./assertNever";
import { flattenChapters, scenePath } from "./zwConfig/scenes";
import type { ManuscriptItem, ZwFirstParagraph } from "./zwConfig/types";

/**
 * Chapter openings (Styling ▸ Chapter opening): the first paragraph of each
 * chapter can start with a drop cap / raised initial or a small-caps lead-in.
 * Shared by the editor (which only styles a chapter's first scene) and export.
 */

/** Words in a "first words" lead-in. */
export const OPENING_WORD_COUNT = 4;

export type OpeningLeadIn = "words" | "sentence";

/** Characters forming the initial: the first letter, with any opening quote
 * or bracket before it (like CSS `::first-letter`). 0 for blank text. */
export function initialLength(text: string): number {
  const match = /^\s*["'“‘([]*\S/u.exec(text);
  return match === null ? 0 : match[0].length;
}

/** The first-paragraph setting in effect for a scene: "chapterFlush" only
 * sets a chapter's first scene flush; other scenes read as "indented". */
export function sceneFirstParagraph(
  setting: ZwFirstParagraph,
  opensChapter: boolean,
): ZwFirstParagraph {
  switch (setting) {
    case "indented":
    case "sceneFlush":
      return setting;
    case "chapterFlush":
      return opensChapter ? setting : "indented";
    default:
      return assertNever(setting);
  }
}

/** Whether `relativePath` is the first scene of a chapter. */
export function isChapterOpeningScene(
  manuscript: ManuscriptItem[],
  relativePath: string,
): boolean {
  return flattenChapters(manuscript).some((chapter) => {
    const first = chapter.scenes[0];
    return (
      typeof first !== "undefined" &&
      scenePath(chapter.folder, first.file) === relativePath
    );
  });
}

const WORD = /\S+/g;
/** A sentence ends at . ! ? or … plus any closing quotes / brackets. */
const SENTENCE_END = /[.!?…]+["'”’)\]]*(?=\s|$)/;

/**
 * Characters at the start of `text` that form the lead-in: the first
 * `OPENING_WORD_COUNT` words, or the first sentence (all of it when it never
 * ends). 0 for blank text.
 */
export function leadInLength(text: string, leadIn: OpeningLeadIn): number {
  if (text.trim() === "") return 0;
  switch (leadIn) {
    case "words": {
      let end = 0;
      let count = 0;
      for (const match of text.matchAll(WORD)) {
        end = match.index + match[0].length;
        count += 1;
        if (count === OPENING_WORD_COUNT) break;
      }
      return end;
    }
    case "sentence": {
      const match = SENTENCE_END.exec(text);
      return match === null
        ? text.trimEnd().length
        : match.index + match[0].length;
    }
    default:
      return assertNever(leadIn);
  }
}
